/**
 * UserMessage Component
 * 
 * Handles the display of user messages in the chat interface.
 * 
 * Features:
 * - Simple text display with proper styling for user messages
 * - Right-aligned layout to distinguish from assistant messages
 * - Responsive design with consistent theming
 * - Clean, readable text formatting
 * - JSON content filtering to prevent display of technical data
 * - Message editing functionality with edit button
 *   - Edit button appears on hover to the left of the message
 *   - When editing, shows textarea with Update and Cancel buttons
 *   - Calls onEdit callback when message is updated
 * - Save as prompt functionality with bookmark button
 *   - Save button appears on hover to the left of the message
 *   - Calls onSaveAsPrompt callback to save message to prompt library
 * - 80% max width constraint includes both action buttons and message
 * 
 * Props:
 * - message: ChatMessage object containing the user's message
 * - className: Optional additional CSS classes for styling
 * - onEdit: Optional callback function (messageId: number, newContent: string) => void
 *           Called when user updates a message
 * - onSaveAsPrompt: Optional callback function (content: string) => void
 *                   Called when user saves message as a prompt template
 * 
 * Logic:
 * - Uses flexbox layout with max-width constraint applied to the container
 * - Action buttons (save, edit) have flex-shrink-0 to maintain their size
 * - Message content area uses flex-1 and min-w-0 to take remaining space
 * - Ensures both action buttons and message content fit within 80% screen width
 * - Filters JSON content before display to maintain message readability
 * 
 * Changes:
 * - Close an active editor when the parent withdraws edit permission.
 * - Added editing state management (isEditing, editedContent)
 * - Added edit button with pencil icon that appears on hover
 * - Added editing UI with textarea and Update/Cancel buttons
 * - Added onEdit prop for handling message updates
 * - Enhanced styling to support editing interface
 * - Modified layout to include edit button within 80% width constraint
 * - Added flex-shrink-0 to edit button and flex-1 min-w-0 to message content
 * - Added JSON filtering to prevent display of technical JSON data in user messages
 * - Added save as prompt button with bookmark icon
 * - Added onSaveAsPrompt prop for saving messages to prompt library
 * - Grouped action buttons together with consistent hover behavior
 */

import React, { useEffect, useState } from 'react';
import { ChatMessage } from '../../models/ChatMessage';
import { filterJsonFromUserMessage } from '../../utils/chat';

interface UserMessageProps {
  message: ChatMessage;
  className?: string;
  onEdit?: (messageId: number, newContent: string) => void;
  onSaveAsPrompt?: (content: string) => void;
}

export const UserMessage: React.FC<UserMessageProps> = ({ message, className = '', onEdit, onSaveAsPrompt }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(message.content);
  const trimmedEditedContent = editedContent.trim();
  const trimmedOriginalContent = message.content.trim();
  const hasContentChanged = trimmedEditedContent !== trimmedOriginalContent;
  const canUpdate = !!trimmedEditedContent && hasContentChanged;

  useEffect(() => {
    if (!onEdit && isEditing) {
      setIsEditing(false);
      setEditedContent(message.content);
    }
  }, [isEditing, message.content, onEdit]);

  const handleEditClick = () => {
    setEditedContent(message.content);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedContent(message.content);
  };

  const handleUpdate = () => {
    if (!canUpdate) {
      return;
    }

    if (onEdit) {
      onEdit(message.id, trimmedEditedContent);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      handleUpdate();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEdit();
    }
  };

  if (isEditing && onEdit) {
    return (
      <div className={`flex justify-end ${className}`}>
        <div className="flex flex-col w-full max-w-[80%] p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
          <textarea
            value={editedContent}
            onChange={(e) => setEditedContent(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full p-2 mb-3 text-sm border rounded-md bg-white dark:bg-gray-800 text-black dark:text-white focus:ring-blue-500 focus:border-blue-500 resize-none"
            rows={Math.max(3, editedContent.split('\n').length)}
            autoFocus
            placeholder="Edit your message..."
          />
          <div className="flex gap-2 justify-end">
            <button
              onClick={handleCancelEdit}
              className="px-3 py-1 text-sm bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdate}
              disabled={!canUpdate}
              className="px-3 py-1 text-sm bg-blue-600 dark:bg-blue-700 text-white rounded hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Update
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex justify-end ${className}`}>
      <div className="flex items-center gap-2 group max-w-[80%]">
        {(onEdit || onSaveAsPrompt) && (
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-all duration-200 flex-shrink-0">
            {onSaveAsPrompt && (
              <button
                onClick={() => onSaveAsPrompt(message.content)}
                className="p-1 text-gray-400 dark:text-gray-500 hover:text-yellow-600 dark:hover:text-yellow-400 transition-colors"
                aria-label="Save as prompt"
                title="Save as prompt"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
              </button>
            )}
            {onEdit && (
              <button
                onClick={handleEditClick}
                className="p-1 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                aria-label="Edit message"
                title="Edit message"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
            )}
          </div>
        )}
        <div className="rounded-lg px-4 py-2 bg-blue-600 dark:bg-blue-800 flex-1 min-w-0">
          <div className="text-sm text-white max-w-none whitespace-pre-wrap">
            {filterJsonFromUserMessage(message.content)}
          </div>
        </div>
      </div>
    </div>
  );
};
